import { getSourcePage } from 'Shared/utils/getSourcePage';

// Constants
import TrackingConstants from 'Shared/constants/Tracking.constants';

// Types
import { User } from 'Shared/constants/types';
import { QuickStartGuideItemIDs } from 'Dashboard/components/QuickStartGuide/QuickStartGuideItems.constants';
import { DataSourceSlugs } from 'Shared/constants/types';

export function trackEvent(event: string, properties?: {}, options?: {}) {
  (window as any).analytics.track(event, properties, options);
}

export function trackUserProfilePageClick(currentUser: User, profileSlug: string, sourcePage?: string) {
  const source_page = sourcePage ? sourcePage : getSourcePage();
  trackEvent('Clicked View User Profile', {
    [TrackingConstants.SOURCE_PAGE]: source_page,
    [TrackingConstants.USER_PROFILE.VIEWING_OWN_PROFILE]: currentUser.slug == profileSlug,
  });
}

export function trackInvitedTeammate(invitedUsers: number, inviteMethod: string, invitedFromCalendar = false) {
  trackEvent('Invited Teammate to Workspace (Frontend)', {
    no_invited_teammates: invitedUsers,
    invite_method: inviteMethod,
    invited_from_calendar: invitedFromCalendar,
  });
}

export function trackOpenedModal(modalType: string) {
  trackEvent('Opened Modal', { modal: modalType });
}

export function trackWorkspaceNotificationSidebar(isOpen: boolean, workspaceType: string) {
  const action = isOpen ? 'Collapsed' : 'Expanded';
  trackEvent(`${action} Workspace Notifications Sidebar`, { workspaceType });
}

export function trackClickedNotification(inWorkspaceNotification: boolean, type: string, workspaceType: string) {
  trackEvent('Clicked Notification', {
    inWorkspaceNotification,
    generalNotificationFeed: !inWorkspaceNotification,
    notificationType: type,
    workspaceType,
  });
}

export function trackViewedSurfaceFeed(workspaceType?: string) {
  const inWorkspace = Boolean(workspaceType);
  trackEvent('Viewed Surface Feed', { inWorkspace, inTeamFeed: !inWorkspace, workspaceType });
}

export function trackUsedGoalFilter(filterType: string, filterValue?: string) {
  trackEvent('Used goal filter', { filterType, filterValue });
}

export function trackUsedAdminDashFilter(filterType: string) {
  trackEvent('Used Admin Dashboard Filter', { filterType });
}

export function trackViewedAdminDashMetric(metric: string) {
  trackEvent('Viewed Admin Dashboard Metric', { metric });
}

export function trackSharedAgendaInOnboarding(invitees: number, discussionTopics: number) {
  trackEvent('Shared agenda (onboarding)', { invitees, discussionTopics });
}

export function trackSaw360StartScreen(startScreenType: string) {
  trackEvent('Saw 360 Review Start Screen', { startScreenType });
}

export function trackAddedComment(appId, itemType, workspaceType) {
  trackEvent('Added Comment', { app_id: appId, sourcePage: getSourcePage(), itemType, workspaceType });
}

export function trackOpenRevisitDropdown() {
  trackEvent(TrackingConstants.REVISIT_DISCUSSION_TOPIC_EVENTS.OPENED_REVISIT_DROPDOWN);
}

export function trackOpenRevisitCustomModal() {
  trackEvent(TrackingConstants.REVISIT_DISCUSSION_TOPIC_EVENTS.OPENED_REVISIT_CUSTOM_MODAL);
}

export function trackOpenRevisitSnoozeModal() {
  trackEvent(TrackingConstants.REVISIT_DISCUSSION_TOPIC_EVENTS.OPENED_REVISIT_SNOOZE_MODAL);
}

export function trackClickQuickStartGuide(action: 'open' | 'close') {
  trackEvent(TrackingConstants.QUICK_START_GUIDE.CLICKED_QUICK_START_GUIDE, {
    [TrackingConstants.QUICK_START_GUIDE.ACTION]: action,
  });
}

function integrationNameToAppName(integrationName: string) {
  return (
    {
      [DataSourceSlugs.onedrive]: 'Office 365 OneDrive',
      [DataSourceSlugs.googleCalendarWorkspaceSync]: 'Google Calendar Workspace Sync',
      [DataSourceSlugs.office365Calendar]: 'Office 365 Workspace Sync',
      [DataSourceSlugs.googleDrive]: 'Google Drive',
      [DataSourceSlugs.salesforce]: 'Salesforce',
      [DataSourceSlugs.slackAi]: 'Slack AI',
    }[integrationName] || integrationName
  );
}

export function trackRequestedIntegrationAccess(integrationName: string) {
  trackEvent('Requested Access to Integration', { app: integrationNameToAppName(integrationName) });
}

export function trackClickedConnectApp(integrationName: string, in_onboarding = false) {
  trackEvent('Clicked Connect App Integration', {
    app: integrationNameToAppName(integrationName),
    sourcePage: getSourcePage(),
    in_onboarding,
  });
}

export function trackClickQuickStartGuideItem(itemId: QuickStartGuideItemIDs) {
  trackEvent(TrackingConstants.QUICK_START_GUIDE.CLICKED_QUICK_START_GUIDE_ITEM, {
    [TrackingConstants.QUICK_START_GUIDE.ITEM]: itemId,
  });
}

export function trackClickQuickStartGuideCTA(itemId: QuickStartGuideItemIDs) {
  trackEvent(TrackingConstants.QUICK_START_GUIDE.CLICKED_QUICK_START_GUIDE_CTA, {
    [TrackingConstants.QUICK_START_GUIDE.ITEM]: itemId,
  });
}

export function trackQuickStartGuideItemStatus(
  itemId: QuickStartGuideItemIDs,
  status: 'complete' | 'skipped',
  isQsgOpen: boolean
) {
  trackEvent(TrackingConstants.QUICK_START_GUIDE.COMPLETED_QUICK_START_GUIDE_ITEM, {
    [TrackingConstants.QUICK_START_GUIDE.ITEM]: itemId,
    [TrackingConstants.QUICK_START_GUIDE.STATUS]: status,
    [TrackingConstants.QUICK_START_GUIDE.IS_QUICK_START_GUIDE_OPEN]: isQsgOpen,
  });
}

export function trackQuickStartDismissClick() {
  trackEvent(TrackingConstants.QUICK_START_GUIDE.CLICKED_DISMISS);
}
